import { db } from "../firebaseConfig.js";
import {
  saveNavGridChangesToCloud,
  saveNavGridSegmentToCloud,
} from "./navGridCloudSync.js";

const DB_NAME = "trekkin-navgrid-segment-ops";
const DB_VERSION = 1;
const STORE_NAME = "ops";
const QUEUE_EVENT = "trekkin:navgrid-segment-ops-updated";

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexeddb_unavailable"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: "segmentId" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("navgrid_queue_open_failed"));
  });
}

function gridSummary(grid = {}) {
  return {
    spacingMeters: Number(grid?.spacingMeters) || 50,
    mergeMeters: Number(grid?.mergeMeters) || 15,
    routeMergeMeters: Number(grid?.routeMergeMeters) || 30,
    entryPromptMeters: Number(grid?.entryPromptMeters) || 200,
    segmentCount: Array.isArray(grid?.segments)
      ? grid.segments.length
      : Math.max(0, Number(grid?.segmentCount) || 0),
    updatedAt: Number(grid?.updatedAt) || Date.now(),
  };
}

function dispatchUpdate() {
  try {
    window.dispatchEvent(new CustomEvent(QUEUE_EVENT));
  } catch {}
}

async function putOp(op) {
  const database = await openDb();
  await new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).put(op);
    transaction.oncomplete = resolve;
    transaction.onerror = () => reject(transaction.error || new Error("navgrid_queue_write_failed"));
  });
  database.close();
  dispatchUpdate();
  return op;
}

async function listOps() {
  const database = await openDb();
  const rows = await new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readonly");
    const request = transaction.objectStore(STORE_NAME).getAll();
    request.onsuccess = () => resolve(Array.isArray(request.result) ? request.result : []);
    request.onerror = () => reject(request.error || new Error("navgrid_queue_read_failed"));
  });
  database.close();
  return rows.sort((a, b) => Number(a?.queuedAt || 0) - Number(b?.queuedAt || 0));
}

async function deleteOp(segmentId) {
  const database = await openDb();
  await new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).delete(segmentId);
    transaction.oncomplete = resolve;
    transaction.onerror = () => reject(transaction.error || new Error("navgrid_queue_delete_failed"));
  });
  database.close();
  dispatchUpdate();
}

export async function enqueueNavGridSegmentUpsert(segment, grid = {}) {
  const segmentId = String(segment?.id || "").trim();
  if (!segmentId) throw new Error("navgrid_segment_id_missing");
  return putOp({
    segmentId,
    type: "upsert",
    segment: { ...(segment || {}), id: segmentId },
    grid: gridSummary(grid),
    queuedAt: Date.now(),
  });
}

export async function enqueueNavGridSegmentRemove(segmentId, grid = {}) {
  const id = String(segmentId || "").trim();
  if (!id) throw new Error("navgrid_segment_id_missing");
  return putOp({
    segmentId: id,
    type: "remove",
    segment: null,
    grid: gridSummary(grid),
    queuedAt: Date.now(),
  });
}

export async function syncOrQueueNavGridSegment(segment, grid = {}) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    await enqueueNavGridSegmentUpsert(segment, grid);
    return { ok: true, queued: true };
  }
  try {
    await saveNavGridSegmentToCloud(db, segment, grid);
    return { ok: true, queued: false };
  } catch (error) {
    await enqueueNavGridSegmentUpsert(segment, grid);
    return { ok: true, queued: true, error: String(error?.message || error) };
  }
}

export async function syncOrQueueNavGridSegmentRemove(segmentId, grid = {}) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    await enqueueNavGridSegmentRemove(segmentId, grid);
    return { ok: true, queued: true };
  }
  try {
    await saveNavGridChangesToCloud(db, { removedIds: [segmentId], grid });
    return { ok: true, queued: false };
  } catch (error) {
    await enqueueNavGridSegmentRemove(segmentId, grid);
    return { ok: true, queued: true, error: String(error?.message || error) };
  }
}

export async function flushNavGridSegmentOpsQueue() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { ok: false, reason: "offline", pending: (await listOps().catch(() => [])).length };
  }
  const ops = await listOps();
  let applied = 0;
  for (const op of ops) {
    try {
      if (op.type === "remove") {
        await saveNavGridChangesToCloud(db, {
          removedIds: [op.segmentId],
          grid: op.grid || {},
        });
      } else {
        await saveNavGridSegmentToCloud(db, op.segment, op.grid || {});
      }
      await deleteOp(op.segmentId);
      applied += 1;
    } catch (error) {
      return {
        ok: false,
        reason: "sync_failed",
        applied,
        pending: ops.length - applied,
        error: String(error?.message || error),
      };
    }
  }
  return { ok: true, applied, pending: 0 };
}

let initialized = false;
let flushing = false;

export function initNavGridSegmentOpsAutoFlush() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  const run = async () => {
    if (flushing || navigator.onLine === false) return;
    flushing = true;
    try {
      await flushNavGridSegmentOpsQueue();
    } catch {
      // Mantém a operação em IndexedDB para a próxima tentativa.
    } finally {
      flushing = false;
    }
  };
  window.addEventListener("online", run);
  window.addEventListener(QUEUE_EVENT, run);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") run();
  });
  window.setTimeout(run, 1800);
}

export { QUEUE_EVENT as NAVGRID_SEGMENT_OPS_EVENT };

